import React, { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import PrivateRoute from "./privateRoute";
import { Flex, useBreakpointValue, Box } from "@chakra-ui/react";
import { motion } from "framer-motion";
import Header from "./header";
import Sidebar from "./sidebar";
import { BrandSplash } from "./ui";
import { ease, reducedTransition, useReducedMotion } from "../theme";
import { axiosClient } from "../client/http-client";
import { useMutation } from "@tanstack/react-query";
import { useBranch } from "../data/user";
import { UserContext } from "../data/userContext";
import type { CustomerDetails } from "../types/customerDetailsTypes";

// Re-exported so existing imports (`import { CustomerDetails } from "./appWrapper"`) keep working.
export type { CustomerDetails } from "../types/customerDetailsTypes";

interface AppWrapperProps {}

interface CustomerResponse {
  message: string;
  data: CustomerDetails;
}

// Below `lg` the sidebar becomes a Drawer opened from the header hamburger.
const drawerVariant = { navigation: "drawer", navigationButton: true };
const sidebarVariant = { navigation: "sidebar", navigationButton: false };

const AppWrapper: React.FC<AppWrapperProps> = () => {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const { setBranch } = useBranch();
  const location = useLocation();
  const reduce = useReducedMotion();

  const variants = useBreakpointValue({ base: drawerVariant, lg: sidebarVariant });
  const toggleSidebar = () => setSidebarOpen(!isSidebarOpen);

  const getCustomerData = async (): Promise<CustomerDetails> => {
    const response = await axiosClient.post<CustomerResponse>(
      "/applications/customerDetails/customerDetails",
    );
    return response.data.data;
  };

  const { data: userData, isPending, mutate } = useMutation({
      mutationFn: getCustomerData,
      onSuccess: (data) => {
        setBranch(data.branch.value);
      }
    });

    useEffect(() => {
        mutate();
      }, [mutate]);

  if (isPending || !userData) {
    return <BrandSplash />;
  }

  return (
    <PrivateRoute>
      <UserContext.Provider value={userData ?? null}>
        <Flex h="100vh" overflow="hidden" bg="surface.canvas">
          <Sidebar
            CloseAfterSelect={toggleSidebar}
            isOpen={isSidebarOpen}
            onClose={toggleSidebar}
            variant={variants?.navigation}
            userData={userData ?? null}
            isPending={isPending}
          />

          {/* The right column is the scroll container so the glass header floats over the content.
              position="relative" keeps absolutely positioned descendants (e.g. VisuallyHidden screen-reader text)
              inside this scroller; without it they escape to the page and let the window scroll past the content. */}
          <Flex direction="column" flex={1} minW={0} h="100%" overflowY="auto" overflowX="hidden" position="relative">
            <Header
              showSidebarButton={variants?.navigationButton}
              onShowSidebar={toggleSidebar}
            />

            <Box
              as="main"
              flex={1}
              minW={0}
              bg="surface.canvas"
              px={{ base: 4, md: 6, xl: 8 }}
              py={{ base: 4, md: 6 }}
            >
              <Box maxW="1440px" mx="auto">
                <motion.div
                  key={location.pathname}
                  initial={reduce ? false : { opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={reduce ? reducedTransition : { duration: 0.2, ease: ease.standard }}
                >
                  <Outlet />
                </motion.div>
              </Box>
            </Box>
          </Flex>
        </Flex>
      </UserContext.Provider>
    </PrivateRoute>
  );
};

export default AppWrapper;
